// A bounded display-only cache. Checkout always validates the current configured Stripe Price.
export function cardPriceKey(product) {
  return product?.isActive === true && product.listingType === 'native' && product.isFree === false &&
    product.stripeConfig?.enabled === true && product.stripeConfig?.priceId && product.id ?
    JSON.stringify([product.id, product.stripeConfig.priceId]) : '';
}

export function createCardPriceLoader(fetchPrice, { concurrency = 4, now = Date.now,
  ttl = 60000, failureTtl = 5000, capacity = 200 } = {}) {
  const cache = new Map();
  const queue = [];
  let running = 0;
  function completed() { running -= 1; drain(); }
  function drain() {
    while (running < concurrency && queue.length) {
      const job = queue.shift();
      running += 1;
      Promise.resolve().then(job.run).then(job.resolve, job.reject).finally(completed);
    }
  }
  return function loadCardPrice(product) {
    const key = cardPriceKey(product);
    if (!key) return Promise.resolve(null);
    const existing = cache.get(key);
    if (existing && existing.expires > now()) return existing.promise;
    const entry = { expires: Infinity };
    entry.promise = new Promise((resolve, reject) => {
      queue.push({ resolve, reject, run: async () => {
        try {
          const price = await fetchPrice(product.id);
          if (price?.productId !== product.id || price?.priceId !== product.stripeConfig.priceId) {
            throw new Error('The configured card price changed. Refresh to check the current price.');
          }
          entry.expires = now() + ttl;
          return price;
        } catch {
          entry.expires = now() + failureTtl;
          throw new Error('Card pricing is unavailable. Open the product to check again.');
        }
      } });
      drain();
    });
    cache.set(key, entry);
    // Evict settled entries first; in-flight entries remain deduplicated.
    for (const [cachedKey, cached] of cache) {
      if (cache.size <= capacity) break;
      if (cached.expires !== Infinity) cache.delete(cachedKey);
    }
    return entry.promise;
  };
}
